import React, {
  useState,
  useEffect,
  useCallback,
  useContext,
  createContext,
  Fragment,
} from 'react';
import PropTypes from 'prop-types';
import ScrollLock from 'react-scrolllock';

import defaultTheme from './theme';
import Arrow from './components/Arrow';
import Container from './components/Container';
import Footer from './components/Footer';
import Header from './components/Header';
import PaginatedThumbnails from './components/PaginatedThumbnails';
import Portal from './components/Portal';
import DefaultSpinner from './components/Spinner';
import { deepMerge } from './utils/util';
import Modal from './components/Modal';

function normalizeSourceSet(data) {
  const sourceSet = data?.srcSet || data?.srcset;
  return Array.isArray(sourceSet) ? sourceSet.join() : sourceSet;
}

const ThemeContext = createContext({
  theme: defaultTheme,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  toggleTheme: () => {},
});

const ImgsViewer = ({
  currImg,
  enableKeyboardInput = true,
  imgCountSeparator = ' / ',
  imgs,
  isOpen,
  leftArrowTitle,
  onClickImg,
  onClickNext,
  onClickPrev,
  onClickThumbnail,
  onClose,
  preloadNextImg = true,
  preventScroll = true,
  rightArrowTitle,
  showImgCount = true,
  showThumbnails,
  spinnerDisabled = false,
  spinner: Spinner = DefaultSpinner,
  spinnerColor = '#fff',
  spinnerSize = 50,
  theme = {},
  thumbnailOffset = 2,
  width = 1024,
}) => {
  const mergedTheme = deepMerge(defaultTheme, theme);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [currentTheme, setCurrentTheme] = useState(mergedTheme);

  // Corrected toggleTheme function
  const toggleTheme = useCallback(() => {
    setCurrentTheme((prevTheme) =>
      prevTheme === defaultTheme ? mergedTheme : defaultTheme
    );
  }, [mergedTheme]);

  const gotoNext = useCallback(
    (event) => {
      if (imgLoaded && currImg < imgs.length - 1) {
        event?.preventDefault();
        event?.stopPropagation();
        onClickNext();
      }
    },
    [currImg, imgs.length, imgLoaded, onClickNext]
  );

  const gotoPrev = useCallback(
    (event) => {
      if (imgLoaded && currImg > 0) {
        event?.preventDefault();
        event?.stopPropagation();
        onClickPrev();
      }
    },
    [currImg, imgLoaded, onClickPrev]
  );

  const handleKeyboardInput = useCallback(
    (event) => {
      const { keyCode } = event;
      if ([37, 33, 38].includes(keyCode)) {
        gotoPrev(event);
      } else if ([39, 34, 40].includes(keyCode)) {
        gotoNext(event);
      } else if ([27, 32].includes(keyCode)) {
        onClose();
      }
    },
    [gotoNext, gotoPrev, onClose]
  );

  useEffect(() => {
    if (isOpen && enableKeyboardInput) {
      window.addEventListener('keydown', handleKeyboardInput);
    }
    return () => {
      window.removeEventListener('keydown', handleKeyboardInput);
    };
  }, [isOpen, enableKeyboardInput, handleKeyboardInput]);

  const preloadImgData = (data, onload) => {
    if (!data) return;
    const img = new Image();
    const sourceSet = normalizeSourceSet(data);

    img.onload = onload;
    img.onerror = onload;
    img.src = data.src;
    if (sourceSet) img.srcset = sourceSet;

    return img;
  };

  const preloadImg = (idx, onload) => {
    if (imgs[idx]) {
      preloadImgData(imgs[idx], onload);
    }
  };

  const handleImgLoaded = useCallback(() => setImgLoaded(true), []);

  useEffect(() => {
    if (preloadNextImg) {
      preloadImg(currImg + 1, handleImgLoaded);
      preloadImg(currImg - 1, handleImgLoaded);
    }
    if (typeof currImg === 'number') {
      preloadImg(currImg, handleImgLoaded);
    }
  }, [currImg, preloadNextImg]);

  const renderArrowPrev = () =>
    currImg === 0 ? null : (
      <Arrow
        theme={currentTheme}
        direction="left"
        icon="arrowLeft"
        onClick={gotoPrev}
      />
    );

  const renderArrowNext = () =>
    currImg === imgs.length - 1 ? null : (
      <Arrow
        theme={currentTheme}
        direction="right"
        icon="arrowRight"
        onClick={gotoNext}
      />
    );

  const renderImgs = () => {
    if (!imgs || !imgs.length) return null;
    const img = imgs[currImg];
    const sourceSet = normalizeSourceSet(img);

    const thumbnailsSize = showThumbnails ? currentTheme.thumbnail.size : 0;
    const heightOffset = `${
      currentTheme.header.height +
      currentTheme.footer.height +
      thumbnailsSize +
      currentTheme.container.gutter.vertical
    }px`;

    return (
      <figure className="m-0 rounded-xl overflow-hidden">
        <img
          className={`block mx-auto max-w-full max-h-full object-contain transition-opacity duration-300 ${
            imgLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={onClickImg}
          sizes="90vw"
          alt={img?.alt || ''}
          src={img?.src || ''}
          srcSet={sourceSet}
          style={{
            cursor: onClickImg ? 'pointer' : 'auto',
            maxHeight: `calc(80vh - ${heightOffset})`,
            maxWidth: '90vw', // Ensure it takes full width up to the viewport width
          }}
        />
      </figure>
    );
  };

  const renderThumbnails = () =>
    showThumbnails ? (
      <PaginatedThumbnails
        theme={currentTheme}
        leftTitle={leftArrowTitle}
        rightTitle={rightArrowTitle}
        currImg={currImg}
        imgs={imgs}
        offset={thumbnailOffset}
        onClickThumbnail={onClickThumbnail}
      />
    ) : null;

  const renderHeader = () => null;

  const renderFooter = () => (
    <Footer
      theme={currentTheme}
      caption={imgs[currImg]?.caption || ''}
      countCurr={currImg + 1}
      countSeparator={imgCountSeparator}
      countTotal={imgs.length}
      showCount={showImgCount}
    />
  );

  const renderSpinner = () =>
    spinnerDisabled ? null : (
      <div
        className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 transition-opacity duration-300 ${
          !imgLoaded ? 'opacity-100' : 'opacity-0'
        }`}
      >
        <Spinner color={spinnerColor} size={spinnerSize} />
      </div>
    );

  const renderDialog = () => {
    const offsetThumbnails = showThumbnails
      ? currentTheme.thumbnail.size + currentTheme.container.gutter.vertical
      : 0;
    return (
      <Modal onClose={onClose} show={isOpen} size="full">
        <>
          <div
            className={`relative mb-[${offsetThumbnails}px] max-w-[${width}px]`}
          >
            {imgLoaded && renderHeader()}
            {renderImgs()}
            {renderSpinner()}
            {imgLoaded && renderFooter()}
          </div>
          {imgLoaded && renderThumbnails()}
          {imgLoaded && renderArrowPrev()}
          {imgLoaded && renderArrowNext()}
          {preventScroll && <ScrollLock />}
        </>
      </Modal>
    );
  };

  return (
    <ThemeContext.Provider value={{ theme: currentTheme, toggleTheme }}>
      <Portal>{renderDialog()}</Portal>
    </ThemeContext.Provider>
  );
};

ImgsViewer.propTypes = {
  backdropCloseable: PropTypes.bool,
  closeBtnTitle: PropTypes.string,
  currImg: PropTypes.number,
  customControls: PropTypes.arrayOf(PropTypes.node),
  enableKeyboardInput: PropTypes.bool,
  imgCountSeparator: PropTypes.string,
  imgs: PropTypes.arrayOf(
    PropTypes.shape({
      src: PropTypes.string.isRequired,
      srcSet: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
      caption: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
      thumbnail: PropTypes.string,
    })
  ).isRequired,
  isOpen: PropTypes.bool,
  leftArrowTitle: PropTypes.string,
  onClickImg: PropTypes.func,
  onClickNext: PropTypes.func,
  onClickPrev: PropTypes.func,
  onClickThumbnail: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  preloadNextImg: PropTypes.bool,
  preventScroll: PropTypes.bool,
  rightArrowTitle: PropTypes.string,
  showCloseBtn: PropTypes.bool,
  showImgCount: PropTypes.bool,
  showThumbnails: PropTypes.bool,
  spinnerDisabled: PropTypes.bool,
  spinner: PropTypes.func,
  spinnerColor: PropTypes.string,
  spinnerSize: PropTypes.number,
  theme: PropTypes.object,
  thumbnailOffset: PropTypes.number,
  width: PropTypes.number,
};

export default ImgsViewer;
